/**
 * @file Components/Tours/NegotiationModal.jsx
 * @description Modal for price negotiation on large group bookings.
 * Collects customer info, proposed price, and submits as a negotiation query.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, DollarSign, Mail, Phone, User, MessageSquare, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import queryService from '../../services/queryService';

const NegotiationModal = ({ isOpen, onClose, tour, guestCount }) => {
  const [form, setForm] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    proposedPrice: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const originalTotal = tour ? tour.price * guestCount : 0;
  const groupTotal = tour?.pricing?.group ? tour.pricing.group * guestCount : originalTotal;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customerName || !form.customerEmail || !form.proposedPrice) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await queryService.createQuery({
        type: 'negotiation',
        customerName: form.customerName,
        customerEmail: form.customerEmail,
        customerPhone: form.customerPhone,
        subject: `Price Negotiation: ${tour.title} (${guestCount} guests)`,
        content: form.message || `I'd like to negotiate the price for ${guestCount} guests on ${tour.title}. My proposed total is ₹${form.proposedPrice}.`,
        tourId: tour._id,
        negotiation: {
          proposedPrice: parseFloat(form.proposedPrice),
          guestCount,
          originalPrice: originalTotal,
        },
      });
      setSuccess(true);
      toast.success('Negotiation request sent! We\'ll get back to you soon.');
    } catch (err) {
      toast.error(err?.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#243185] to-[#1a2460] p-5 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white">Negotiate Price</h3>
              <p className="text-white/70 text-sm">{tour?.title}</p>
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white p-1">
              <X size={20} />
            </button>
          </div>

          {success ? (
            <div className="p-8 text-center">
              <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-gray-900 mb-2">Request Submitted!</h4>
              <p className="text-gray-500 mb-4">Our team will review your proposal and get back to you within 24 hours.</p>
              <button onClick={onClose} className="text-[#243185] font-medium hover:underline">Close</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Price comparison */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-500">Original price ({guestCount} guests)</span>
                  <span className="font-semibold text-gray-900">${originalTotal.toLocaleString()}</span>
                </div>
                {groupTotal < originalTotal && (
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-500">Group rate</span>
                    <span className="font-semibold text-green-600">${groupTotal.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm border-t border-gray-200 pt-2 mt-2">
                  <span className="text-gray-700 font-medium flex items-center gap-1">
                    <Users size={14} /> {guestCount} guests
                  </span>
                  <span className="text-xs text-gray-400">Propose your price below</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 sm:col-span-1">
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Full Name *"
                      value={form.customerName}
                      onChange={(e) => setForm(p => ({ ...p, customerName: e.target.value }))}
                      className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none"
                      required
                    />
                  </div>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      placeholder="Email *"
                      value={form.customerEmail}
                      onChange={(e) => setForm(p => ({ ...p, customerEmail: e.target.value }))}
                      className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none"
                      required
                    />
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      placeholder="Phone"
                      value={form.customerPhone}
                      onChange={(e) => setForm(p => ({ ...p, customerPhone: e.target.value }))}
                      className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none"
                    />
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="number"
                      placeholder="Your Total Price *"
                      value={form.proposedPrice}
                      onChange={(e) => setForm(p => ({ ...p, proposedPrice: e.target.value }))}
                      className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none"
                      required
                      min={1}
                    />
                  </div>
                </div>
              </div>

              <div className="relative">
                <MessageSquare size={16} className="absolute left-3 top-3 text-gray-400" />
                <textarea
                  placeholder="Additional notes (optional)"
                  value={form.message}
                  onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))}
                  rows={3}
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#243185] text-white py-3 rounded-xl font-semibold hover:bg-[#1a2460] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : 'Submit Negotiation Request'}
              </button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NegotiationModal;
